/**
 * Role Constants
 * Single source of truth for all role types and role-based access rules.
 */

export type RoleType = string | null | undefined;

// All known Strapi role types
export const ROLE_TYPES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  OPERATOR: 'operator',
  KIOSK_USER: 'kiosk_user',
  SALES_OFFICE_USER: 'sales_office_user',
  FINANCE_ADMIN: 'finance_admin',
  SECURITY_GUARD: 'security_guard',
  SAFETY_MANAGER: 'safety_manager',
  SERVICES_MANAGER: 'services_manager',
  TENANT_ADMIN: 'tenant_admin',
  TENANT_USER: 'tenant_user',
  CUSTOMER_OPERATOR: 'customer_operator',
  EMPLOYEE: 'employee',
  CUSTOMER: 'customer',
  ECOMMERCE: 'ecommerce',
  AUTHENTICATED: 'authenticated',
  PUBLIC: 'public',
  SCALE_OPERATOR: 'scale_operator',
  SCALE_ADMIN: 'scale_admin',
  PACKHOUSE_MANAGER: 'packhouse_manager',
  EXECUTIVE: 'executive',
  HEAD_OF_SALES: 'head_of_sales',
  SALES_MANAGER: 'sales_manager',
  SECURITY_MANAGER: 'security_manager',
  IT: 'it',
  VISITOR_APPROVAL_ADMIN: 'visitor_approval_admin',
  SCM_ADMIN: 'scm_admin',
} as const;

// Gate portal: routes scale_operator / scale_admin are allowed to access
export const GATE_PORTAL_SCALE_ALLOWED_ROUTES = [
  '/weighbridge-ops',
  '/weighbridge-corrections',
  '/support/tickets',
] as const;

// Roles with admin-level portal access (elevated permissions)
export const ADMIN_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.OPERATOR,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
] as const;

// Roles that can access the admin portal UI
export const ADMIN_PORTAL_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.PACKHOUSE_MANAGER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
  ROLE_TYPES.EXECUTIVE,
  ROLE_TYPES.HEAD_OF_SALES,
  ROLE_TYPES.SALES_MANAGER,
  ROLE_TYPES.IT,
] as const;

// Roles that bypass tenant requirement (no tenant needed to login)
export const TENANT_BYPASS_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SECURITY_GUARD,
  ROLE_TYPES.SAFETY_MANAGER,
  ROLE_TYPES.SERVICES_MANAGER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
  ROLE_TYPES.EMPLOYEE,
  ROLE_TYPES.PACKHOUSE_MANAGER,
  ROLE_TYPES.EXECUTIVE,
  ROLE_TYPES.HEAD_OF_SALES,
  ROLE_TYPES.SALES_MANAGER,
  ROLE_TYPES.SECURITY_MANAGER,
  ROLE_TYPES.IT,
  ROLE_TYPES.VISITOR_APPROVAL_ADMIN,
  ROLE_TYPES.SCM_ADMIN,
] as const;

// Roles that get admin init data on login
export const ADMIN_INIT_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.OPERATOR,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
  ROLE_TYPES.PACKHOUSE_MANAGER,
  ROLE_TYPES.EXECUTIVE,
  ROLE_TYPES.HEAD_OF_SALES,
  ROLE_TYPES.SALES_MANAGER,
  ROLE_TYPES.IT,
] as const;

// Privileged roles that can use tenant override header
export const PRIVILEGED_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
  ROLE_TYPES.EXECUTIVE,
  ROLE_TYPES.HEAD_OF_SALES,
  ROLE_TYPES.SALES_MANAGER,
  ROLE_TYPES.IT,
] as const;

// System user roles (users without tenant are system-level)
export const SYSTEM_USER_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.OPERATOR,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SECURITY_GUARD,
  ROLE_TYPES.SAFETY_MANAGER,
  ROLE_TYPES.SERVICES_MANAGER,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.FINANCE_ADMIN,
  ROLE_TYPES.EMPLOYEE,
  ROLE_TYPES.PACKHOUSE_MANAGER,
  ROLE_TYPES.EXECUTIVE,
  ROLE_TYPES.HEAD_OF_SALES,
  ROLE_TYPES.SALES_MANAGER,
  ROLE_TYPES.SECURITY_MANAGER,
  ROLE_TYPES.IT,
  ROLE_TYPES.VISITOR_APPROVAL_ADMIN,
  ROLE_TYPES.SCM_ADMIN,
] as const;

// Gate portal: routes only accessible to super_admin
export const GATE_PORTAL_SUPER_ADMIN_ROUTES = [
  '/factories',
  '/accommodations',
] as const;

// Roles whose data is scoped to a single assigned factory, and which are
// not allowed to change factory context via the header location selector
// (applies to both gate portal and admin portal headers)
export const GATE_FACTORY_SELECTOR_DENIED_ROLE_TYPES = [
  ROLE_TYPES.SECURITY_GUARD,
  ROLE_TYPES.SAFETY_MANAGER,
  ROLE_TYPES.SERVICES_MANAGER,
  ROLE_TYPES.EMPLOYEE,
  ROLE_TYPES.SCALE_OPERATOR,
  ROLE_TYPES.SCALE_ADMIN,
  ROLE_TYPES.PACKHOUSE_MANAGER,
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SCM_ADMIN,
] as const;

// Admin portal: routes the 'admin' role is NOT allowed to access
// (super_admin-only features, Control Tower, and the Gates / Zones / QR Readers
// / Cameras gate-ops pages)
export const ADMIN_ROLE_RESTRICTED_ROUTES = [
  '/system/sap-configuration',
  '/system/alarm-configuration',
  '/system/alarms',
  '/system/hardware-logs',
  '/system/integration-event-logs',
  '/system/control-tower',
  '/gate-ops/gates',
  '/gate-ops/zones',
  '/gate-ops/qr-readers',
  '/gate-ops/cameras',
] as const;

// Admin portal: routes the 'it' role is NOT allowed to access
// (SAP integration, alarm configuration and portal configuration are managed
// by system administrators only)
export const IT_ROLE_RESTRICTED_ROUTES = [
  '/system/sap-configuration',
  '/system/sap-sync',
  '/system/alarm-configuration',
  '/system/portal-config',
] as const;

// Admin portal: routes finance_admin is allowed to access
export const FINANCE_ADMIN_ALLOWED_ROUTE_PREFIXES = [
  '/orders/fulfillment',
  '/orders/sales-orders',
  '/orders/incoming-materials',
  '/catalog',
  '/finance',
  '/organization/customer-accounts',
  '/organization/customers',
  '/support',
] as const;

// Admin portal: routes the executive role is allowed to access
// (read-only dashboards, KPIs and reports)
export const EXECUTIVE_ALLOWED_ROUTE_PREFIXES = [
  '/',
  '/sales/dashboard',
  '/sales/sub-customers',
  '/sales/targets',
  '/sales/queue-dashboard',
  '/support',
] as const;

// Admin portal: routes the head_of_sales role is NOT allowed to access at all
// (the Procurement group, Certificates and a set of System configuration
// items are fully hidden/blocked; Gate Operations is handled separately below
// since one page — Loading Location Rules — is allowed through)
export const HEAD_OF_SALES_RESTRICTED_ROUTE_PREFIXES = [
  '/orders/incoming-materials',
  '/orders/inbound-shipments',
  '/orders/material-groups',
  '/catalog/certificates',
  '/system/health',
  '/system/sap-sync',
  '/system/sap-configuration',
  '/system/portal-config',
  '/system/alarm-configuration',
  '/system/notification-templates',
  '/system/visit-guidelines',
] as const;

// Admin portal: Gate Operations sub-routes the head_of_sales role can access
// (Loading Location Rules only — the rest of Gate Operations stays hidden/blocked)
export const HEAD_OF_SALES_ALLOWED_GATE_OPS_ROUTE_PREFIXES = [
  '/gate-ops/loading-location-rules',
] as const;

// Admin portal: Organization sub-routes the head_of_sales role can access
// (read-only — customers, sub-customers, customer accounts, onboarding;
// organization/users is excluded)
export const HEAD_OF_SALES_ALLOWED_ORGANIZATION_ROUTE_PREFIXES = [
  '/organization/customers',
  '/organization/sub-customers',
  '/organization/customer-accounts',
  '/organization/onboarding',
] as const;

// Admin portal: Locations sub-routes the head_of_sales role can access
// (Factories, Address Book and Cities only; storage-locations and regions
// remain hidden/blocked)
export const HEAD_OF_SALES_ALLOWED_LOCATIONS_ROUTE_PREFIXES = [
  '/locations/factories',
  '/locations/addressbook',
  '/locations/cities',
] as const;

// Admin portal: routes the sales_manager role is NOT allowed to access
// (storage-locations page, the whole Gate Operations tab, Users management,
// the Procurement group and Certificates)
export const SALES_MANAGER_RESTRICTED_ROUTE_PREFIXES = [
  '/locations/storage-locations',
  '/gate-ops',
  '/organization/users',
  '/orders/incoming-materials',
  '/orders/inbound-shipments',
  '/orders/material-groups',
  '/catalog/certificates',
] as const;

// Admin portal: the only System sub-routes the sales_manager role can access
// (everything else under /system is hidden/blocked)
export const SALES_MANAGER_ALLOWED_SYSTEM_ROUTE_PREFIXES = [
  '/system/simulation',
  '/system/control-tower',
] as const;

// Admin portal: routes the sales_office_user role is NOT allowed to access
// (Sales Dashboard, Sub-Customers Dashboard, Monthly Targets, the Procurement
// group, and the whole Catalog, Locations, Gate Operations, Organization and
// System groups)
export const SALES_OFFICE_USER_RESTRICTED_ROUTE_PREFIXES = [
  '/sales/dashboard',
  '/sales/sub-customers',
  '/sales/targets',
  '/orders/incoming-materials',
  '/orders/inbound-shipments',
  '/orders/material-groups',
  '/catalog',
  '/locations',
  '/gate-ops',
  '/organization',
  '/system',
] as const;

// Roles allowed to mutate visitors (create, update, delete, approve, check-in/out, cancel)
// security_guard can only VIEW visitors
export const VISITOR_MANAGE_ROLE_TYPES = [
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.OPERATOR,
  ROLE_TYPES.SALES_OFFICE_USER,
  ROLE_TYPES.EMPLOYEE,
  ROLE_TYPES.VISITOR_APPROVAL_ADMIN,
] as const;

// Gate portal: routes the employee role is allowed to access
export const GATE_PORTAL_EMPLOYEE_ALLOWED_ROUTES = [
  '/visitors',
  '/settings',
  '/support/tickets',
] as const;

// Gate portal: routes the security_guard role is allowed to access
export const GATE_PORTAL_SECURITY_GUARD_ALLOWED_ROUTES = [
  '/visitors',
  '/access-logs',
  '/support/tickets',
] as const;

// Gate portal: routes the security_manager role is allowed to access
// (same as security_guard, plus departments and employees management)
export const GATE_PORTAL_SECURITY_MANAGER_ALLOWED_ROUTES = [
  '/visitors',
  '/access-logs',
  '/departments',
  '/employees',
  '/support/tickets',
] as const;

// Gate portal: routes the visitor_approval_admin role is allowed to access
export const GATE_PORTAL_VISITOR_APPROVAL_ADMIN_ALLOWED_ROUTES = [
  '/visitors',
  '/departments',
  '/employees',
  '/access-logs',
  '/accommodations',
  '/visit-guidelines',
  '/support/tickets',
] as const;

// Gate portal: routes the services_manager role is allowed to access
export const GATE_PORTAL_SERVICES_MANAGER_ALLOWED_ROUTES = [
  '/visitors',
  '/accommodations',
  '/settings',
  '/support/tickets',
] as const;

// Gate portal: routes the packhouse_manager role is allowed to access
export const GATE_PORTAL_PACKHOUSE_MANAGER_ALLOWED_ROUTES = [
  '/',
  '/loading-locations',
  '/loading-location-rules',
  '/queue-dashboard',
  '/yard',
  '/orders',
  '/support/tickets',
] as const;

// Gate portal: routes the scm_admin role is allowed to access
// (incoming materials + inbound shipments for their assigned plant only)
export const GATE_PORTAL_SCM_ADMIN_ALLOWED_ROUTES = [
  '/incoming-materials',
  '/inbound-shipments',
  '/support/tickets',
] as const;

// Kiosk portal: roles allowed to access the kiosk portal
export const KIOSK_PORTAL_ALLOWED_ROLES = [
  ROLE_TYPES.KIOSK_USER,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
] as const;
